import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Check, ChevronDown } from "lucide-react";
import { useLang } from "@/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LANGS = [
  { code: "es", label: "Español" },
  { code: "ca", label: "Català" },
];

const links = (t) => [
  { id: "history", label: t.nav.history },
  { id: "mission", label: t.nav.mission },
  { id: "services", label: t.nav.services },
  { id: "community", label: t.nav.community },
  { id: "gallery", label: t.nav.gallery },
  { id: "contact", label: t.nav.contact },
];

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled ? "bg-bone/90 backdrop-blur-md border-b border-bone-200 shadow-[0_4px_24px_rgba(26,46,37,0.05)]" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5 group"
          data-testid="nav-logo"
        >
          <img src="/logo.gif" alt="AEMBAIX — Associació d'Esclerosi Múltiple del Baix Llobregat" className="h-12 w-auto object-contain" />
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {links(t).map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="relative text-[15px] font-medium text-pine-800 hover:text-terracotta transition-colors duration-200 after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-terracotta hover:after:w-full after:transition-[width] after:duration-300"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1.5 rounded-full border border-pine-300/60 px-3.5 py-2 text-sm font-semibold text-pine-900 hover:bg-bone-100 transition-colors duration-200"
                aria-label="Cambiar idioma / Canviar idioma"
                data-testid="lang-toggle"
              >
                {lang.toUpperCase()}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[150px]">
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className="flex items-center justify-between cursor-pointer"
                  data-testid={`lang-option-${l.code}`}
                >
                  {l.label}
                  {lang === l.code && <Check className="w-4 h-4 text-terracotta" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            onClick={() => go("contact")}
            className="hidden sm:inline-flex items-center rounded-full bg-terracotta hover:bg-terracotta-600 text-white px-5 py-2.5 text-sm font-semibold transition-colors duration-200"
            data-testid="nav-cta"
          >
            {t.nav.cta}
          </button>

          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid place-items-center w-10 h-10 rounded-full text-pine-900 hover:bg-bone-100 transition-colors"
            aria-label="Menú"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-bone border-b border-bone-200"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {links(t).map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left py-3 text-base font-medium text-pine-800 border-b border-bone-200 last:border-0"
                  data-testid={`mobile-nav-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
